import { useMutation, useQueryClient } from '@tanstack/react-query';
import { i18n } from '@/shared/i18n';
import { enqueueSnackbar } from 'notistack';
import { assetsHttp } from '@/lib/api/http-client';
import { SUPPRESS_ERROR_NOTIFICATION_HEADER } from '@/lib/api/interceptors/problem-details.interceptor';
import { parseProblemDetail } from '@/shared/utils/problem-details';
// C4 bans cross-entity value imports, and the faithful fix is to lift this
// invalidation to the page (the `useWorkspaceAuth` precedent). Kept here on
// purpose: a category write re-files templates SERVER-side, so the refresh has
// to happen on every writer. Lifting it turns a guarantee into something each
// caller must remember, which is the bug the coupling note below describes.
// eslint-disable-next-line no-restricted-syntax -- see above; revisit if a third slice needs this
import { TAXONOMY_FIELD_TEMPLATE_QUERY_KEYS } from '@/entities/taxonomy-field-template';
import type {
  BulkDeleteTaxonomyCategoriesResult,
  CreateTaxonomyCategoryPayload,
  DeleteTaxonomyCategoryResult,
  TaxonomyCategory,
  UpdateTaxonomyCategoryPayload,
} from '../model/types';
import { TAXONOMY_CATEGORY_QUERY_KEYS } from './queries';

const CATEGORIES_URL = '/api/v1/taxonomy-categories';

/**
 * Every writer in this file reports its own failure, so the global interceptor
 * must not also report them.
 *
 * A duplicate name is the expected failure and the backend's message names the
 * clash, which is worth showing — but the interceptor already toasts every
 * Problem Detail, and persistently. Without this header the user got both: the
 * real message they had to dismiss, and a vaguer one beside it.
 */
// eslint-disable-next-line i18next/no-literal-string -- HTTP header value
const SUPPRESS_GLOBAL_TOAST = { [SUPPRESS_ERROR_NOTIFICATION_HEADER]: 'true' };

/**
 * Categories and field templates are coupled by NAME, so a category write can
 * change how fields are grouped (a rename re-points every template server-side).
 * Refreshing both slices keeps the Fields table and the picker rail from
 * disagreeing about which group a field is in.
 */
function useCategoryCacheRefresh() {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: TAXONOMY_CATEGORY_QUERY_KEYS.all });
    void queryClient.invalidateQueries({ queryKey: TAXONOMY_FIELD_TEMPLATE_QUERY_KEYS.all });
  };
}

export function useCreateTaxonomyCategory() {
  const refresh = useCategoryCacheRefresh();

  return useMutation({
    mutationFn: async (payload: CreateTaxonomyCategoryPayload) => {
      const response = await assetsHttp.post({
        throwOnError: true,
        url: CATEGORIES_URL,
        body: payload,
        headers: SUPPRESS_GLOBAL_TOAST,
      });
      return response.data as TaxonomyCategory;
    },
    onSuccess: (created) => {
      refresh();
      enqueueSnackbar(
        i18n.t('knowledge:taxonomy.taxonomyCategoryMutations.categoryCreated', {
          name: created.name,
        }),
        { variant: 'success' },
      );
    },
    onError: (error: unknown) => {
      // `parseProblemDetail`, not `error.body.detail`: the generated client
      // rethrows the raw AxiosError, so the body is on `response.data` and the
      // old read was always undefined — the fallback won every time and the
      // backend's message reached the user only via the interceptor's separate,
      // persistent toast.
      const { detail } = parseProblemDetail(
        error,
        i18n.t('knowledge:taxonomy.taxonomyCategoryMutations.failedCreateCategory'),
      );
      enqueueSnackbar(detail, { variant: 'error' });
    },
  });
}

export function useUpdateTaxonomyCategory(id: string) {
  const refresh = useCategoryCacheRefresh();

  return useMutation({
    mutationFn: async (payload: UpdateTaxonomyCategoryPayload) => {
      const response = await assetsHttp.patch({
        throwOnError: true,
        url: `${CATEGORIES_URL}/{id}`,
        path: { id },
        body: payload,
        headers: SUPPRESS_GLOBAL_TOAST,
      });
      return response.data as TaxonomyCategory;
    },
    onSuccess: () => {
      refresh();
      enqueueSnackbar(i18n.t('knowledge:taxonomy.taxonomyCategoryMutations.categoryUpdated'), {
        variant: 'success',
      });
    },
    onError: (error: unknown) => {
      // See the create handler: the detail lives on the axios response.
      const { detail } = parseProblemDetail(
        error,
        i18n.t('knowledge:taxonomy.taxonomyCategoryMutations.failedUpdateCategory'),
      );
      enqueueSnackbar(detail, { variant: 'error' });
    },
  });
}

/**
 * Seeds the registry from the shipped standard-category catalog.
 *
 * `names` picks a subset; omit it to add everything missing. The backend skips
 * names the registry already holds either way, so a double-click can't produce
 * duplicates — and that idempotence is what makes this the way back for someone
 * who deleted a standard category.
 */
export function useSeedStandardCategories() {
  const refresh = useCategoryCacheRefresh();

  return useMutation({
    mutationFn: async (names?: string[]) => {
      const response = await assetsHttp.post({
        throwOnError: true,
        url: `${CATEGORIES_URL}/seed-standard`,
        body: names?.length ? { names } : {},
        headers: SUPPRESS_GLOBAL_TOAST,
      });
      return (response.data ?? []) as TaxonomyCategory[];
    },
    onSuccess: (created) => {
      refresh();
      enqueueSnackbar(
        created.length === 0
          ? i18n.t('knowledge:taxonomy.taxonomyCategoryMutations.yourRegistryAlreadyHasEvery')
          : i18n.t('knowledge:taxonomy.taxonomyCategoryMutations.addedStandardCategories', {
              count: created.length,
            }),
        { variant: created.length === 0 ? 'info' : 'success' },
      );
    },
    onError: (error: unknown) => {
      const { detail } = parseProblemDetail(
        error,
        i18n.t('knowledge:taxonomy.taxonomyCategoryMutations.failedAddStandardCategories'),
      );
      enqueueSnackbar(detail, { variant: 'error' });
    },
  });
}

export function useDeleteTaxonomyCategory() {
  const refresh = useCategoryCacheRefresh();

  return useMutation({
    mutationFn: async (id: string) => {
      const response = await assetsHttp.delete({
        throwOnError: true,
        url: `${CATEGORIES_URL}/{id}`,
        path: { id },
        headers: SUPPRESS_GLOBAL_TOAST,
      });
      return response.data as DeleteTaxonomyCategoryResult;
    },
    onSuccess: (result) => {
      refresh();
      // Where the fields went is the part worth saying out loud — the delete
      // moved them, and a user who doesn't know that will go looking for
      // fields they think are gone.
      const moved = result.movedFieldCount;
      const movedFields: string = i18n.t(
        'knowledge:taxonomy.taxonomyCategoryMutations.fieldCount',
        { count: moved },
      );
      enqueueSnackbar(
        moved > 0
          ? i18n.t('knowledge:taxonomy.taxonomyCategoryMutations.categoryRemovedFieldsMoved', {
              fields: movedFields,
              target: result.movedTo ?? '',
            })
          : i18n.t('knowledge:taxonomy.taxonomyCategoryMutations.categoryRemoved'),
        { variant: 'success' },
      );
    },
    onError: (error: unknown) => {
      const { detail } = parseProblemDetail(
        error,
        i18n.t('knowledge:taxonomy.taxonomyCategoryMutations.failedRemoveCategory'),
      );
      enqueueSnackbar(detail, { variant: 'error' });
    },
  });
}

/**
 * Removes several categories at once. Their fields are re-filed exactly as a
 * single delete does, and the server totals the move so this reports one number
 * instead of a toast per row.
 */
export function useBulkDeleteTaxonomyCategories(options?: { onSuccess?: () => void }) {
  const refresh = useCategoryCacheRefresh();

  return useMutation({
    mutationFn: async (ids: string[]) => {
      const response = await assetsHttp.post({
        throwOnError: true,
        url: `${CATEGORIES_URL}/bulk-delete`,
        body: { ids },
        headers: SUPPRESS_GLOBAL_TOAST,
      });
      if (!response.data) throw new Error('Bulk delete failed');
      return response.data as BulkDeleteTaxonomyCategoriesResult;
    },
    onSuccess: (result) => {
      refresh();
      const n = result.deletedIds.length;
      const moved = result.movedFieldCount;
      const deleted: string = i18n.t(
        'knowledge:taxonomy.taxonomyCategoryMutations.deletedCategories',
        { count: n },
      );
      const movedFields: string = i18n.t(
        'knowledge:taxonomy.taxonomyCategoryMutations.fieldCount',
        { count: moved },
      );
      enqueueSnackbar(
        moved > 0
          ? i18n.t('knowledge:taxonomy.taxonomyCategoryMutations.deletedCategoriesFieldsMoved', {
              deleted,
              fields: movedFields,
              target: result.movedTo ?? '',
            })
          : deleted,
        { variant: 'success' },
      );
      options?.onSuccess?.();
    },
    onError: (error: unknown) => {
      const { detail } = parseProblemDetail(
        error,
        i18n.t('knowledge:taxonomy.taxonomyCategoryMutations.failedDeleteCategories'),
      );
      enqueueSnackbar(detail, { variant: 'error' });
    },
  });
}
